import { Eye, CheckCircle, X, RefreshCcw, User, CreditCard } from "lucide-react"
import type { TableAction } from "@shared/types/data-table"
import type { Refund } from "@shared/types/refunds"
import { createBaseActions, mergeActions, type BaseActionHandlers } from "./base-actions"
import { CopyButton } from "@shared/components/ui/CopyButton"

interface RefundSpecificHandlers {
  onProcessRefund?: (refund: Refund) => void
  onCancelRefund?: (refund: Refund) => void
  onRetryRefund?: (refund: Refund) => void
  onViewPayment?: (refund: Refund) => void
  onViewCustomer?: (refund: Refund) => void
  onPreview?: (refund: Refund) => void
  onBulkCancel?: (refundIds: string[]) => void
}

export type RefundActionHandlers = BaseActionHandlers<Refund> & RefundSpecificHandlers

// Status conditions for refund actions
const isPending = (refund: Refund) =>
  refund.status === "pending" || refund.status === "refund_requested"

const isFailed = (refund: Refund) =>
  refund.status === "failed" || refund.status === "refund_failed" || refund.status === "refund_declined"

const createRefundRowActions = (handlers: RefundActionHandlers) => [
  {
    key: "preview",
    label: "Preview",
    icon: <Eye className="h-4 w-4" />,
    onClick: (refund: Refund) => handlers.onPreview?.(refund),
  },
  {
    key: "process",
    label: "Approve refund",
    icon: <CheckCircle className="h-4 w-4" />,
    onClick: (refund: Refund) => handlers.onProcessRefund?.(refund),
    condition: isPending,
  },
  {
    key: "retry",
    label: "Retry refund",
    icon: <RefreshCcw className="h-4 w-4" />,
    onClick: (refund: Refund) => handlers.onRetryRefund?.(refund),
    condition: isFailed,
  },
  {
    key: "cancel",
    label: "Cancel refund",
    icon: <X className="h-4 w-4" />,
    onClick: (refund: Refund) => handlers.onCancelRefund?.(refund),
    variant: "destructive" as const,
    condition: isPending,
  },
  {
    key: "view-payment",
    label: "View payment",
    icon: <CreditCard className="h-4 w-4" />,
    onClick: (refund: Refund) => handlers.onViewPayment?.(refund),
  },
  {
    key: "view-customer",
    label: "View customer",
    icon: <User className="h-4 w-4" />,
    onClick: (refund: Refund) => handlers.onViewCustomer?.(refund),
  },
  // Note: flag, copy-id, and add-note come from base actions (createBaseActions)
]


export const getRefundActions = (handlers: RefundActionHandlers = {}) => {
  const baseActions = createBaseActions<Refund>('Refund', handlers, {
    showEdit: false,
    showDelete: false,
    showFlag: true,
    showCopyId: true,
    showAddNote: true,
    showExport: true,
  })

  return mergeActions(baseActions, {
    bulkActions: [
      {
        key: 'cancel',
        label: 'Cancel Selected',
        icon: <X className="h-4 w-4" />,
        variant: 'destructive' as const,
        onClick: (ids: string[]) => handlers.onBulkCancel?.(ids),
        disabled: (ids: string[]) => ids.length === 0,
      },
    ],
    rowActions: createRefundRowActions(handlers),
  })
}

export const getRefundBulkActions = (handlers: RefundActionHandlers = {}) =>
  getRefundActions(handlers).bulkActions

export const getRefundRowActions = (handlers: RefundActionHandlers = {}) =>
  getRefundActions(handlers).rowActions

export const getRefundHoverActions = (handlers: RefundActionHandlers = {}): TableAction<Refund>[] => [
  {
    key: "copy-id",
    label: "Copy Refund ID",
    onClick: () => {},
    renderAction: (refund) => (
      <CopyButton value={refund.id} className="h-7 w-7" buttonSize="sm" />
    ),
  },
  {
    key: "process",
    label: "Process",
    icon: <CheckCircle className="h-4 w-4" />,
    onClick: (refund) => handlers.onProcessRefund?.(refund),
    condition: (refund) => refund.status === "pending",
  },
  {
    key: "retry",
    label: "Retry",
    icon: <RefreshCcw className="h-4 w-4" />,
    onClick: (refund) => handlers.onRetryRefund?.(refund),
    condition: (refund) => refund.status === "failed",
  },
]
